import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { useMemo } from 'react'

import type { TimeRange } from '~/data/time/types'
import { useUserPrefs } from '~/hooks/useUserPrefs'
import { dispatcherFactory, selectorHookFactory } from '~/store/utils'

type SerializableTimeRange = {
  from: string
  to: string
}

export type ChargingOptimizationState = {
  timeRange?: SerializableTimeRange
}

const initialState: ChargingOptimizationState = {}

const chargingOptimizationSlice = createSlice({
  name: 'chargingOptimization',
  initialState,
  reducers: {
    // Store the start and end of the selected time window as ISO strings so they survive a page reload
    setTimeRange(state, action: PayloadAction<TimeRange | undefined>) {
      if (!action.payload) {
        state.timeRange = undefined
        return
      }

      state.timeRange = {
        from: action.payload.from.toISOString(),
        to: action.payload.to.toISOString()
      }
    },
    // Quick utility in case we ever need to clear everything at once
    reset: () => initialState
  }
})

export default chargingOptimizationSlice.reducer

const selectSlice = (state: { chargingOptimization: ChargingOptimizationState }) => state.chargingOptimization

const useSerializedTimeRange = selectorHookFactory((state) => selectSlice(state).timeRange)

// Hook used by the page to read back the saved time range
export function useChargingOptimizationTimeRange(): TimeRange | undefined {
  const range = useSerializedTimeRange()
  const { tzDayjs } = useUserPrefs()

  // Convert the saved ISO strings back into dayjs objects so the UI can keep using its helpers
  return useMemo(() => {
    if (!range) return undefined
    return {
      from: tzDayjs(range.from),
      to: tzDayjs(range.to)
    }
  }, [range, tzDayjs])
}

// Hook used by the page to push new choices into the store
export const useSetChargingOptimizationTimeRange = dispatcherFactory(chargingOptimizationSlice.actions.setTimeRange)

export const { reset } = chargingOptimizationSlice.actions
